import type { TranslationResources } from "./pt";

// English translations. Must mirror every key in pt.ts.
const en: TranslationResources = {
  common: {
    update: "Refresh",
    loading: "Loading data…",
    viewAll: "View all",
    manage: "Manage",
    waiting: "Waiting...",
    reading: "Reading...",
    ok: "OK",
  },
  language: {
    label: "Language",
    pt: "Portuguese",
    en: "English",
  },
  theme: {
    light: "Light theme",
    dark: "Dark theme",
  },
  brand: {
    title: "Printing Platform",
  },
  nav: {
    sections: {
      operacao: "Operation",
      impressao: "Printing",
      importacao: "Import",
      gravacao: "Encoding",
      configuracao: "Configuration",
      ajuda: "Help",
    },
    items: {
      inicio: "Home",
      etiquetas: "Labels",
      conferirEtiqueta: "Check Label",
      logsImpressao: "Print Logs",
      filaImpressao: "Print Queue",
      impressaoAvulsa: "Single Print",
      impressaoSscc: "SSCC Print",
      impressaoBipagem: "Scan Print",
      reimprimirEtiqueta: "Reprint Label",
      importarPlanilha: "Import Spreadsheet",
      importarXml: "Import XML",
      importarPedidos: "Import Orders",
      importarOrdem: "Import Batch",
      gravacaoAvulsa: "Single Encoding",
      serializacao: "Serialization",
      testadorModulo: "Module Tester",
      perfilPreferencia: "Preference Profile",
      preferencias: "Preferences",
      documentacao: "Documentation",
    },
    copyIds: "Copy IDs (debug)",
    copyIdsTooltip: "Copy IDs (account, user and configuration)",
    idsCopied: "IDs copied to clipboard",
  },
  routes: {
    login: "Login",
    inicio: "Home",
    etiquetas: "Labels",
    editorEtiqueta: "Label Editor",
    conferirEtiqueta: "Check Label",
    importarOrdem: "Import Batch",
    impressaoAvulsa: "Single Print",
    gravacaoAvulsa: "Single Encoding",
    reimprimirEtiqueta: "Reprint Label",
    importarPlanilha: "Import Spreadsheet",
    importarXml: "Import XML",
    logsImpressao: "Print Logs",
    filaImpressaoSocket: "Socket Print Queue",
    serializacao: "Serialization",
    testadorModulo: "Module Tester",
    importarPedidos: "Import Orders",
    impressaoSscc: "SSCC Print",
    impressaoBipagem: "Scan Print",
    perfilPreferencia: "Preference Profile",
    preferencias: "Preferences",
    documentacao: "Documentation",
  },
  header: {
    moduleStatus: "Module status",
    moduleStatusFull: "Module status",
    moduleConfig: "Module config",
    moduleConfigFull: "Module settings",
    checkLabel: "Check label",
  },
  user: {
    admin: "Administrator",
    preferences: "Preferences",
    logout: "Log out",
    noName: "No name",
  },
  auth: {
    heroTitle: "Printing Platform",
    heroSubtitle:
      "Manage your RFID labels efficiently with full control over the printing process.",
    feature1: "Real-time RFID encoding and reading",
    feature2: "Multiple printing profiles per hardware",
    feature3: "Live order logs and diagnostics",
    rightsReserved: "All rights reserved.",
  },
  dashboard: {
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    fallbackUser: "user",
    summary: "Here is a summary of your printing operation.",
    noProfile: "No profile",
    profileTitle: "Printing profile in use",
    stats: {
      last7days: "Last 7 days",
      ordersPrinted: "orders printed",
      successRate: "Success rate",
      finished: "{{count}} finished",
      failed: "Failed",
      checkLogs: "check the logs",
      allGood: "all good",
      currentQueue: "Current queue",
      onePending: "pending order",
      manyPending: "pending orders",
    },
    statsError: "Could not load all statistics. Please try refreshing again.",
    roll: {
      level: "Label roll level",
      ending: "Running out",
      usedOf: "of",
      labelsUsed: "labels used",
      remaining: "remaining",
    },
    quickActions: "Quick actions",
    actions: {
      impressaoAvulsaDesc: "Print a single label",
      importarPlanilhaDesc: "CSV or Excel",
      importarXmlDesc: "Invoices",
      importarPedidosDesc: "Manufacturing orders",
      importarOrdemDesc: "List of active batches",
      reimprimirEtiquetaDesc: "Repeat an existing label",
      conferirEtiquetaDesc: "Validate RFID reading",
      logsImpressaoDesc: "History and diagnostics",
    },
    recentPrints: "Recent prints",
    noRecentPrints: "No recent prints",
    labels: "labels",
    printQueue: "Print queue",
    queueEmpty: "Empty queue",
    queueEmptyDesc: "Nothing waiting to print",
    pending: "Pending",
    andMore: "and {{count}} more…",
    hardware: "Hardware",
    defaultLabel: "Default label",
  },
  login: {
    success: "Signed in successfully!",
    invalidCredentials:
      "Incorrect username or password. Check your credentials and try again.",
    autoLoginFailed:
      "Could not authenticate with the provided link. Please sign in manually.",
    authenticating: "Authenticating…",
    authenticatingDesc: "Please wait a moment while we sign you in.",
    welcome: "Welcome back",
    subtitle: "Enter your credentials to access your account",
    userOrEmail: "Username or Email",
    password: "Password",
    forgotPassword: "Forgot your password?",
    hidePassword: "Hide password",
    showPassword: "Show password",
    capsLock: "Caps Lock is on",
    signingIn: "Signing in...",
    signIn: "Sign in",
    noAccount: "Don't have an account?",
    signUp: "Sign up",
  },
  cadastro: {
    title: "Create your account",
    subtitle: "Start managing your RFID labels in a few steps",
    sectionCompany: "About the company",
    sectionAccess: "Your access",
    companyName: "Company name",
    companyNamePlaceholder: "E.g.: Acme Industries Ltd",
    corporateEmail: "Corporate email",
    corporateEmailPlaceholder: "contact@company.com",
    phone: "Phone",
    cnpj: "Tax ID",
    optional: "(optional)",
    fullName: "Full name",
    fullNamePlaceholder: "Your full name",
    accessEmail: "Access email",
    accessEmailPlaceholder: "you@company.com",
    password: "Password",
    passwordPlaceholder: "Minimum 8 characters",
    confirmPassword: "Confirm password",
    confirmPasswordPlaceholder: "Repeat the password",
    capsLock: "Caps Lock is on",
    acceptTermsPrefix: "I have read and accept the",
    termsOfUse: "terms of use",
    and: "and the",
    privacyPolicy: "privacy policy",
    creating: "Creating account...",
    submit: "Create account",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    successTitle: "Registration complete!",
    successDesc:
      "Your account was created successfully. For security reasons, new accounts start out awaiting approval from our support team.",
    nextSteps: "Next steps to enable your access",
    nextStep1: "Get in touch with our support team",
    nextStep2Prefix: "Provide the registered email:",
    backToLogin: "Back to login",
    heroTitle: "Get started in minutes",
    heroSubtitle:
      "Create your account now and take the first step toward a smarter RFID printing operation.",
    errCompanyName: "Enter the company name.",
    errCompanyEmail: "Enter the company email.",
    errEmailInvalid: "Invalid email.",
    errAdminName: "Enter the administrator's name.",
    errAdminEmail: "Enter the access email.",
    errPasswordRequired: "Create a password.",
    errPasswordMin: "The password must be at least 8 characters.",
    errConfirmRequired: "Confirm the password.",
    errPasswordMismatch: "The passwords do not match.",
    errTerms: "You must accept the terms.",
    errCompanyCreate: "Could not create the company.",
    errCompanyEmailInUse: "This email is already in use.",
    errAdminCreate: "Could not create the administrator user.",
    errAdminEmailInUse: "This email is already registered.",
    errCompanyCreatedUserFailed:
      "The company was created, but there was a problem creating the user: {{msg}}. Please contact support.",
  },
  notFound: {
    code: "Error 404",
    title: "Page not found",
    description:
      "The page you are looking for does not exist, has been moved, or is currently unavailable.",
    triedToAccess: "You tried to access:",
    goBack: "Go back",
    goHome: "Go to home",
  },
  common2: {
    save: "Save",
    cancel: "Cancel",
    ok: "OK",
    sync: "Sync",
    print: "Print",
    notDefined: "Not defined",
    understood: "Got it",
  },
  fila: {
    orderName: "Order Name",
    creationDate: "Creation Date",
    actions: "Actions",
    resend: "Resend",
    remove: "Remove",
    title: "Print Queue",
    clearQueue: "Clear Queue",
    empty: "No items in the print queue.",
  },
  ajusteRolo: {
    success: "Roll adjusted successfully!",
    error: "Error adjusting the roll. Please try again.",
    adjusting: "Adjusting the roll, please wait...",
    title: "Automatic Roll Adjustment",
    module: "Module:",
    start: "Start Adjustment",
    adjustAgain: "Adjust Again",
    tryAgain: "Try Again",
  },
  perfilDialog: {
    title: "Module Profile Configuration",
    description: "Configure your printing module's parameters",
  },
  serializacao: {
    codeEmpty: "Code cannot be empty.",
    serialInvalid: "Serial must be a number greater than or equal to zero.",
    edit: "Edit",
    codeGtin: "Code (GTIN)",
    serial: "Serial",
    saveChanges: "Save changes",
    syncSuccess: "Serials synced!",
    noSerialsToDelete: "There are no serials to delete.",
    allDeleted: "All serials were deleted successfully.",
    deleteError: "Error deleting some serials. Please try again.",
    currentSerial: "Current serial",
    actions: "Actions",
    delete: "Delete",
    restrictedTitle: "Restricted access",
    restrictedDesc: "Only administrators can access the serialization page.",
    deleteAllConfirm:
      "Are you sure you want to delete all registered serials? This action cannot be undone.",
    deleteAllTitle: "Delete all serials",
    deleteOneConfirm:
      "Are you sure you want to delete this serial? This action cannot be undone.",
    deleteOneTitle: "Delete serial",
    title: "Serialization",
    subtitle:
      "Control the current serial of each GTIN (next number generated when printing)",
    deleteAll: "Delete all",
    noSerialsToDeleteTitle: "There are no serials to delete",
    registeredSerials: "Registered serials",
    codesCount_one: "{{count}} code",
    codesCount_other: "{{count}} codes",
    noSerials: "No serials registered",
    noSerialsDesc:
      "Serials appear here automatically after the first print of each code.",
  },
  ordens: {
    syncSuccess: "Data synced successfully!",
    orderName: "Order name",
    actions: "Actions",
    print: "Print",
    delete: "Delete",
    deleteConfirm:
      "Are you sure you want to delete this print order? This action cannot be undone.",
    deleteTitle: "Delete print order",
    selectAll: "Select all orders",
    selectOrder: "Select order",
    deleteSelected: "Delete selected",
    deleteSelectedTitle: "Delete selected orders",
    deleteSelectedConfirm_one:
      "Are you sure you want to delete the selected order? This action cannot be undone.",
    deleteSelectedConfirm_other:
      "Are you sure you want to delete the {{count}} selected orders? This action cannot be undone.",
    deleteAll: "Delete all",
    deleteAllTitle: "Delete all orders",
    deleteAllConfirm_one:
      "Are you sure you want to delete the available order? This action cannot be undone.",
    deleteAllConfirm_other:
      "Are you sure you want to delete all {{count}} orders? This action cannot be undone.",
    title: "Import Batch",
    subtitle: "Orders available for printing coming from the ERP",
    sync: "Sync",
    available: "Available orders",
    ordersCount_one: "{{count}} order",
    ordersCount_other: "{{count}} orders",
    noOrders: "No orders available",
    noOrdersDesc:
      'When the ERP sends new orders, they will appear here. Use "Sync" to check.',
    viewJson: "JSON",
    jsonTitle: "Order JSON",
    copyJson: "Copy JSON",
    jsonCopied: "JSON copied to clipboard.",
    jsonCopyError: "Could not copy the JSON.",
    jsonError: "Error fetching the order JSON. Please try again later.",
    printedOrders: "Printed orders",
    printedOrdersTitle: "Orders already marked as printed",
    printedOrdersDesc:
      "Orders already printed or deleted. Use the JSON button to see the original content.",
    noPrintedOrders: "No order marked as printed",
    syncNow: "Sync now",
    configNotFound: "Printing configuration not found.",
    selectLocation: "Select a location to print.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    fieldsNotConfigured:
      "Printing fields not configured correctly. Configure them in Preferences.",
    errorCreatingCategory: "Error creating category with code {{codigo}}.",
    noEpcGenerated: "No EPC generated for printing.",
    invalidEpcs: "Invalid EPCs found. Check the data and try again.",
    errorCollecting: "Error collecting order data. Please try again later.",
    codeField: "Code (Field {{n}})",
    descField: "Description (Field {{n}})",
    epcField: "EPC (Field {{n}})",
    loc1Field: "Location 1 (Field {{n}})",
    loc2Field: "Location 2 (Field {{n}})",
    loc3Field: "Location 3 (Field {{n}})",
    loc4Field: "Location 4 (Field {{n}})",
    externalCodeField: "External Code (Field {{n}})",
    field: "Field {{n}}",
    notDefinedParen: "(Not defined)",
    repeatedEpcsFound: "Repeated EPCs found",
    repeatedEpcsDesc:
      'The EPCs below already exist in the database. Sync the data and try again, or enable "Allow repeated" to print anyway.',
    noOrderName: "Order name not provided",
    noOrderNameDesc: "Go back to the list and select an order to view.",
    backToList: "Back to list",
    loadingOrder: "Loading order",
    backToListTitle: "Back to list",
    itemsInOrder_one: "{{count}} item in the order",
    itemsInOrder_other: "{{count}} items in the order",
    helpTooltip:
      'To set the code and description of items, go to "Preferences" and then click "Order (Socket)".',
    allowRepeated: "Allow repeated",
    noItemsToPrint: "No items to print",
    selectALocation: "Select a location",
    printNLabels_one: "Print {{count}} label",
    printNLabels_other: "Print {{count}} labels",
    codesConverted:
      "{{count}} code(s) with characters outside the hexadecimal standard were converted automatically.",
    codesConvertedDesc:
      "The original code comes from the order, but the EPC will be generated with the converted code.",
    locations: "Locations",
    level1: "Level 1",
    level2: "Level 2",
    level3: "Level 3",
    level4: "Level 4",
    orderItems: "Order items",
    itemsCount_one: "{{count}} item",
    itemsCount_other: "{{count}} items",
  },
  planilha: {
    ean: "EAN",
    description: "Description",
    value: "Value",
    quantity: "Quantity",
    expiry: "Expiry Date",
    location1: "Location 1",
    location2: "Location 2",
    location3: "Location 3",
    location4: "Location 4",
    infoCompl: "Add. info {{n}}",
    notArray: "The data is not an array.",
    rowNotObject: "Item on row {{n}} is not a valid object.",
    requiredFieldsMissing:
      "Required fields missing or empty in the spreadsheet: {{details}}.",
    fieldOnLines: '"{{field}}" on row(s) {{lines}}',
    andMore: "and {{n}} more",
    requiredByEpc:
      "The fields {{fields}} are part of the EPC composition configured for this account, so they must be filled in on rows without TAG.",
    headerAssumed: "Spreadsheet without a header row",
    headerAssumedDesc:
      "No known column was found in the first row, so the columns were assumed in the official template order:",
    tagMustBeString: 'Field "TAG" must be a string on row {{n}}.',
    onlyCsv: "Only .csv files are accepted.",
    importSuccess: "Spreadsheet imported successfully!",
    noFileSelected: "No file selected.",
    syncSuccess: "Data synced successfully!",
    noItemsToPrint: "No items in the spreadsheet to generate printing.",
    configNotFound: "Printing configuration not found.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    reverseWithTags:
      "Cannot print with reverse encoding and TAGs. Please remove the TAGs from the spreadsheet.",
    categoryNotFoundBase: "Category with EAN {{ean}} not found in the database.",
    location1Missing: "Location 1 not provided or invalid for item {{n}}.",
    nv2NotLinked: 'Location L2 "{{desc}}" is not linked to a valid L1 location.',
    nv3NotLinked: 'Location L3 "{{desc}}" is not linked to a valid L2 location.',
    nv4NotLinked: 'Location L4 "{{desc}}" is not linked to a valid L3 location.',
    categoryNotFound: "Category with EAN {{ean}} not found.",
    noEpcGenerated: "No EPC generated for printing.",
    invalidEpcs: "Invalid EPCs found. Check the data and try again.",
    title: "Import Spreadsheet",
    subtitle: "Upload a CSV with items and generate labels in bulk",
    examples: "Examples",
    downloadModel: "Download CSV template",
    items: "Items",
    withoutTag: "without TAG",
    withTag: "with TAG",
    importToPrint: "Import a spreadsheet to print",
    printNLabels_one: "Print {{count}} label",
    printNLabels_other: "Print {{count}} labels",
    importCsvFile: "Import CSV file",
    dropHere: "Drop the file here",
    dragOrClick: "Drag the CSV or click to select",
    acceptsOnly: "Accepts only",
    useExamples: "· use the examples as a template",
    fileLoaded: "File loaded",
    itemImported_one: "{{count}} item imported",
    itemImported_other: "{{count}} items imported",
    clear: "Clear",
    codesConverted:
      "{{count}} code(s) with characters outside the hexadecimal standard were converted automatically.",
    codesConvertedDesc:
      "The original code is kept in the spreadsheet for display, but the EPC will be generated with the converted code.",
    sheetItems: "Spreadsheet items",
    itemsCount_one: "{{count}} item",
    itemsCount_other: "{{count}} items",
    repeatedEpcsFound: "Repeated EPCs found",
    repeatedEpcsDesc:
      'The EPCs below already exist in the database. Sync the data and try again, or enable "Allow repeated" to print anyway.',
  },
  xml: {
    code: "Code",
    codeField: "Code ({{campo}})",
    description: "Description",
    descField: "Description ({{campo}})",
    descBase: "Description (base)",
    info: "Info {{n}}",
    withSource: "{{label}} ({{campo}})",
    withBase: "{{label}} (base)",
    value: "Value",
    valueField: "Value ({{campo}})",
    validity: "Expiry",
    validityField: "Expiry ({{campo}})",
    codeFieldNotConfigured:
      "XML Code field not configured. Configure it in Preferences > XML.",
    invalidXml: "Invalid XML file: {{erro}}",
    processErrorGeneric: "Error processing the XML file.",
    noElementFound:
      'No element with field "{{campo}}" found in the XML. Check the configuration in Preferences > XML.',
    noValidItem: "No item with a valid code found in the XML.",
    someWithoutDesc:
      '{{count}} item(s) without description found. Check the "Description Field" in the XML settings.',
    categoriesNotFound:
      "No valid category found in the database for the provided code(s): {{codigos}}",
    importSuccessN: "{{count}} item(s) imported successfully!",
    importedItems: "Imported items",
    onlyXml: "Only .xml files are accepted.",
    noProducts: "No product found in the XML.",
    noFileSelected: "No file selected.",
    syncSuccess: "Data synced successfully!",
    noItemsToPrint: "No items to generate printing.",
    configNotFound: "Printing configuration not found.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    categoryNotFound: "Category with EAN {{ean}} not found.",
    noEpcGenerated: "No EPC generated for printing.",
    invalidEpcs: "Invalid EPCs found. Check the data and try again.",
    title: "Import XML",
    subtitle: "Import the items from an invoice (.xml file)",
    importToPrint: "Import an XML to print",
    printNLabels_one: "Print {{count}} label",
    printNLabels_other: "Print {{count}} labels",
    importXmlFile: "Import XML file",
    dropHere: "Drop the file here",
    dragOrClick: "Drag the XML or click to select",
    acceptsOnly: "Accepts only",
    ofNfe: "invoice files",
    fileLoaded: "File loaded",
    itemImported_one: "{{count}} item imported",
    itemImported_other: "{{count}} items imported",
    clear: "Clear",
    xmlItems: "XML items",
    itemsCount_one: "{{count}} item",
    itemsCount_other: "{{count}} items",
    repeatedEpcsFound: "Repeated EPCs found",
    repeatedEpcsDesc:
      'The EPCs below already exist in the database. Sync the data and try again, or enable "Allow repeated" to print anyway.',
  },
  ui: {
    search: "Search...",
    noResults: "No results found.",
    noData: "No data found",
    previous: "Previous",
    next: "Next",
    page: "Page",
    of: "of",
    loading: "Loading...",
    selectColumn: "Select a column",
    selectColumnAria: "Select a column to filter",
    filterValue: "Filter value",
    clearFilters: "Clear Filters",
    firstPage: "First page",
    previousPage: "Previous page",
    nextPage: "Next page",
    lastPage: "Last page",
    sureTitle: "Are you sure?",
    sureDesc:
      "This action cannot be undone. It will permanently delete the data.",
    cancel: "Cancel",
    confirm: "Confirm",
  },
  loader: {
    syncing: "Syncing...",
    syncingSlow:
      "Syncing... (may take a little longer depending on the data size)",
    syncingStill: "Still syncing... Please wait a few more moments.",
    syncingLong:
      "Syncing... (this is taking longer than usual, check your connection)",
    syncingContactSupport:
      "Syncing... (if you think it's taking too long, please contact technical support)",
  },
  avulsa: {
    qty: "Qty:",
    val: "Exp:",
    assign: "+ Assign",
    quantity: "Quantity",
    valueBrl: "Value (R$)",
    validity: "Expiry",
    save: "Save",
    zplNotConfigured: "ZPL not configured",
    preview: "Preview",
    previewAlt: "Preview",
    openInNewTab: "Open in new tab",
    descCodeRequired: "Description and code are required.",
    codeConverted: "Code converted to hexadecimal: {{original}} → {{novo}}",
    categoryCreated: "Category created successfully!",
    categoryCreateError: "Error creating category.",
    syncSuccess: "Data synced successfully!",
    selectCategory: "Select a category to print.",
    configNotFound: "Printing configuration not found.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    selectLocation: "Select a location to print.",
    noEpcGenerated: "No EPC generated for printing.",
    invalidEpcs: "Invalid EPCs found. Check the data and try again.",
    individualInfo: "Individual information",
    previewCol: "Preview",
    infoCompl: "Add. Info {{n}}",
    title: "Single Print",
    subtitle: "Print RFID labels from a category",
    showMore: "Show more info",
    allowRepeated: "Allow repeated",
    sync: "Sync",
    selectCategoryLocationToPrint: "Select a category and location to print",
    printNLabels_one: "Print {{count}} label",
    printNLabels_other: "Print {{count}} labels",
    print: "Print",
    category: "Category",
    categoryAndQuantity: "Category and quantity",
    selectACategory: "Select a category",
    noCategoryFound: "No category found.",
    newCategory: "New category",
    searchByDescOrCode: "Search by description, code or extra info...",
    newCategoryTitle: "Create new category",
    quantityLabel: "Quantity",
    locations: "Locations",
    level1: "Level 1",
    level2: "Level 2",
    level3: "Level 3",
    level4: "Level 4",
    description: "Description",
    labels: "Labels",
    generatedLabels: "Generated labels",
    labelsCount_one: "{{count}} label",
    labelsCount_other: "{{count}} labels",
    itemInfo: "Item information",
    selectCategoryToStart: "Select a category to start",
    selectCategoryToStartDesc:
      "Choose an existing category above or create a new one. The item fields and the labels table will appear here.",
    newCategoryDialogTitle: "New category",
    newCategoryDialogDesc: "Create a new category to use as a printing template.",
    createCategory: "Create category",
    descRequired: "Description",
    descPlaceholder: "E.g.: White Small T-shirt",
    codeRequired: "Code",
    codePlaceholder: "EAN or hexadecimal code",
    cancel: "Cancel",
    create: "Create",
    repeatedEpcsFound: "Repeated EPCs found",
    repeatedEpcsDesc:
      'The EPCs below already exist in the database. Sync the data and try again, or enable "Allow repeated" to print anyway.',
  },
  impressao: {
    verifyingTags: "Checking tags in the field before starting the order...",
    wait: "Please wait",
    printerUnavailable: "Printer not connected or unavailable.",
    tagNotFound: "Tag not found or invalid.",
    noItemToReprint: "No item in the queue to reprint.",
    noItemToRewrite: "No item in the queue to re-encode.",
    noZplToRewrite: "Tag has no ZPL to re-encode.",
    usbNotReverseCompatible: "USB printing is not compatible with reverse encoding.",
    usbConnectionError: "Error connecting to the USB printer. Check the connection.",
    usbPrintError: "Error printing via USB: {{msg}}",
    legacyNotReverseCompatible:
      "Legacy printing is not compatible with reverse encoding.",
    finishedSuccess: "Printing finished successfully!",
    statePrinted: "Printed",
    stateSending: "Sending",
    stateError: "Error",
    stateWaiting: "Waiting",
    estado: "Status",
    pausedMsg: "Printing paused.",
    sendingMsg: "Your tags are being sent to the printer.",
    printError: "Printing error",
    pausedTitle: "Printing paused",
    printingTitle: "Printing tags",
    followDesc: "Track the sending of your tags to the printer.",
    close: "Close",
    tryAgain: "Try again",
    cancel: "Cancel",
    resume: "Resume",
    pause: "Pause",
  },
  reimprimir: {
    syncSuccess: "Data synced successfully!",
    configNotFound:
      "Printing configuration not found. Check the settings and try again.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    noItemSelected: "No item selected to reprint.",
    noZpl: "Selected item has no ZPL to reprint.",
    generateError: "Error generating the print list.",
    infoCompl: "Add. Info {{n}}",
    individualInfo: "Individual information",
    qty: "Qty:",
    val: "Exp:",
    preview: "Preview",
    previewBtn: "Preview",
    previewAlt: "Preview",
    openInNewTab: "Open in new tab",
    zplUnavailable: "ZPL unavailable",
    actions: "Actions",
    details: "Details",
    title: "Reprint Label",
    subtitle:
      "Search for an already-printed RFID label and send it to the printer again",
    sync: "Sync",
    selectToReprint: "Select a label to reprint",
    reprintThis: "Reprint this label",
    reprint: "Reprint",
    searchLabel: "Search label",
    typeOrScan: "Type or scan the EPC...",
    clearEpc: "Clear EPC",
    back: "Back",
    search: "Search",
    readWithModule: "Read with module",
    readWithModuleHelp:
      "Triggers the connected RFID reader. The first tag detected fills the field automatically and fetches the data from the database.",
    itemsInBase: "Items in the database",
    ofTotal: "{{count}} of {{total}}",
    noItemFound: "No item found.",
    noResultsFor: "No results for",
    selectedLabel: "Selected label",
    backToList: "Back to list",
    locations: "Locations",
    level: "Level {{n}}",
    itemData: "Item data",
    description: "Description",
    labelTitle: "Label",
  },
  verificar: {
    labelRead: "Label read",
    labelReadDesc: "Details of the label captured by the reading module.",
    searching: "Searching for the label in the database…",
    notFound: "Label not found",
    notFoundDesc: "The label read was not found in the database.",
    epcRead: "EPC read:",
    qty: "Qty:",
    val: "Exp:",
    locations: "Locations",
    level1: "Level 1",
    level2: "Level 2",
    level3: "Level 3",
    level4: "Level 4",
    itemData: "Item data",
    description: "Description",
    infoCompl: "Add. Info {{n}}",
    preview: "Preview",
    open: "Open",
    previewAlt: "Label preview",
    renderError: "Could not render the label.",
    renderedBy: "Rendered via labelary.com",
  },
  conferir: {
    rssi: "RSSI",
    antenna: "Antenna",
    readings: "Readings",
    notConnected: "Module not connected. Check the connection.",
    title: "Check Label",
    subtitle: "Read RFID labels in real time for verification",
    connected: "Connected",
    disconnected: "Disconnected",
    reader: "Reader:",
    notDefined: "Not defined",
    startReading: "Start Reading",
    stopReading: "Stop Reading",
    clear: "Clear",
    totalTags: "Total tags",
    totalReadings: "Total readings",
    status: "Status",
    reading: "Reading...",
    stopped: "Stopped",
    readTags: "Read Tags",
    tag: "tag",
    tags: "tags",
    noTagsRead: "No tags read",
    noTagsReadDesc:
      'Click "Start Reading" and bring the labels close to the RFID reader.',
  },
  etiquetas: {
    configNotFound: "Printing configuration not found.",
    updateSuccess: "Labels updated successfully!",
    updateError: "Error updating labels.",
    nameRequired: "Enter a name for the label.",
    zplRequired: "Enter the label's ZPL.",
    name: "Name",
    default: "Default",
    preview: "Preview",
    viewLabel: "View label",
    actions: "Actions",
    makeDefault: "Make default",
    edit: "Edit",
    cannotRemoveDefault: "Cannot remove the default label",
    removeLabel: "Remove label",
    remove: "Remove",
    title: "Labels",
    subtitle: "Manage your printing profile's label models (ZPL)",
    newLabel: "New label",
    registeredModels: "Registered models",
    labelsCount_one: "{{count}} label",
    labelsCount_other: "{{count}} labels",
    noLabels: "No labels registered",
    noLabelsDesc: "Create your first ZPL label model to start printing.",
    editLabel: "Edit label",
    editDesc: "Update the label's name or ZPL content.",
    newDesc: "Register a new label model with its ZPL code.",
    labelName: "Label name",
    labelNamePlaceholder: "E.g.: Default label 100x50",
    zplCode: "ZPL code",
    previewBtn: "Preview",
    cancel: "Cancel",
    saveChanges: "Save changes",
    createLabel: "Create label",
  },
  gravacao: {
    typeTag: "Type a tag to add.",
    onlyHex: "The tag must contain only hexadecimal characters (0-9, A-F).",
    alreadyAdded: "This tag has already been added.",
    quantityPositive: "The quantity must be greater than zero.",
    addAtLeastOne: "Add at least one tag to encode.",
    configNotFound: "Printing configuration not found.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    singleEncoding: "Single encoding",
    actions: "Actions",
    title: "Single Encoding",
    subtitle: "Encode EPCs onto RFID tags without linking to items",
    addTagsToWrite: "Add tags to encode",
    writeNTags_one: "Encode {{count}} tag",
    writeNTags_other: "Encode {{count}} tags",
    write: "Encode",
    manualTag: "Manual tag",
    add: "Add",
    onlyHexHint: "Hexadecimal characters only (0-9, A-F).",
    generateRandom: "Generate random",
    generate: "Generate",
    generateRandomHint: "Generates valid random EPCs for encoding.",
    tagsToWrite: "Tags to encode",
    tag: "tag",
    tags: "tags",
    clear: "Clear",
    noTagsAdded: "No tags added",
    noTagsAddedDesc: "Add tags manually or generate random ones to get started.",
  },
  perfilModulo: {
    actions: "Actions",
    registeredProfiles: "Registered profiles",
    profilesCount_one: "{{count}} profile",
    profilesCount_other: "{{count}} profiles",
    configIdNotFound: "Configuration ID not found.",
    makeDefault: "Make default",
    alreadyDefault: "Already the default profile",
    edit: "Edit",
    editName: "Edit name",
    profileName: "Profile name",
    typeProfileName: "Enter the profile name",
    nameEmpty: "The profile name cannot be empty.",
    updateSuccess: "Profile updated successfully!",
    editError: "Error editing profile.",
    invalidIdEdit: "Invalid ID or account for editing.",
    save: "Save",
    clone: "Clone",
    cloneProfile: "Clone profile",
    newProfileName: "New profile name",
    typeNewProfileName: "Enter the new profile name",
    cloneSuffix: "(copy)",
    clonedProfileFallback: "Cloned profile (copy)",
    cloneSuccess: "Profile cloned successfully!",
    cloneError: "Error cloning profile.",
    invalidIdClone: "Invalid ID or account for cloning.",
    delete: "Delete",
    cannotDeleteDefault: "Cannot delete the default profile",
    deleteProfile: "Delete profile",
    idAccountNotFound: "ID or account not found.",
    deleteSuccess: "Profile deleted successfully!",
    deleteError: "Error deleting profile.",
    createSuccess: "New profile created successfully!",
    createError: "Error creating profile.",
    editErrorRetry: "Error editing profile, please try again.",
    noName: "no name",
    multipleDefaultWarning:
      'There were {{count}} profiles marked as default. We kept "{{nome}}" and unmarked the others.',
    profile: "Profile",
    default: "Default",
    deleteConfirm:
      'Are you sure you want to delete the profile "{{nome}}"? This action cannot be undone.',
    deleteConfirmGeneric:
      "Are you sure you want to delete this profile? This action cannot be undone.",
    deleteConfigTitle: "Delete configuration profile",
    title: "Configuration Profiles",
    subtitle: "Manage the account's printing configuration profiles",
    defaultLabel: " · default: ",
    newProfile: "New profile",
    createProfile: "Create profile",
    noProfiles: "No profiles registered",
    noProfilesDesc: "Create a new profile to start configuring printing.",
    createFirstProfile: "Create first profile",
  },
  sscc: {
    quantity: "Quantity",
    valueBrl: "Value (R$)",
    validity: "Expiry",
    assign: "Assign",
    noZplFound: "No ZPL found",
    zplNotConfigured: "ZPL not configured",
    view: "View",
    preview: "Preview",
    previewAlt: "Preview",
    openInNewTab: "Open in new tab",
    syncSuccess: "Data synced successfully!",
    selectCompany: "Select a company to print.",
    configNotFound: "Printing configuration not found.",
    informHardwareId: "Enter the Hardware ID in the settings to print.",
    selectLocation: "Select a location to print.",
    errorCreatingCategory: "Error creating category for the selected company.",
    noEpcGenerated: "No EPC generated for printing.",
    invalidEpcs: "Invalid EPCs found. Check the data and try again.",
    individualInfoOptional: "Individual information (optional)",
    individualInfo: "Individual information",
    preview2: "Preview",
    allowRepeatedItems: "Allow repeated items",
    company: "Company",
    locations: "Locations",
    locationN1: "Location Lv.1",
    locationN2: "Location Lv.2",
    locationN3: "Location Lv.3",
    locationN4: "Location Lv.4",
    item: "Item",
    description: "Description",
    labels: "Labels",
    repeatedEpcsTitle: "Repeated EPCs Found",
    repeatedEpcsDesc: "The following EPCs already exist in the database:",
    epcLabel: "EPC:",
    codeLabel: "Code:",
    syncToContinue: "To continue, sync and try again",
    title: "SSCC Print",
    subtitle: "Generate and print SSCC (Serial Shipping Container Code) by company",
    allowRepeated: "Allow repeated",
    selectCompanyLocationToPrint: "Select a company and location to print",
    printNLabels_one: "Print {{count}} label",
    printNLabels_other: "Print {{count}} labels",
    companyAndQuantity: "Company and quantity",
    selectACompany: "Select a company",
    level1: "Level 1",
    level2: "Level 2",
    level3: "Level 3",
    level4: "Level 4",
    generatedLabels: "Generated labels",
    labelsCount_one: "{{count}} label",
    labelsCount_other: "{{count}} labels",
    selectCompanyToStart: "Select a company to start",
    ssccGeneratedByPrefix:
      "SSCCs will be generated according to the GS1 prefix of the chosen company.",
    repeatedEpcsFound: "Repeated EPCs found",
    repeatedEpcsNewDesc:
      'The EPCs below already exist in the database. Sync the data and try again, or enable "Allow repeated" to print anyway.',
  },
  modulo: {
    noTagReadInTime:
      "No tag was read in time. Check that the module is connected and try again.",
    notDetected:
      "Your module does not seem to have been detected. Check the connection and try again.",
    configNotFound: "Printing configuration not found. ",
    configUpdated: "Printing module settings updated successfully.",
    configUpdateError: "Error updating printing module settings.",
    readings: "Readings",
    dataTitle: "Module data",
    dataDesc: "Information collected from the connected hardware.",
    status: "Status",
    available: "Available for printing",
    busy: "Busy / unavailable",
    hardware: "Hardware",
    hardwareId: "Hardware ID",
    firmwareVersion: "Firmware version",
    wifiConnection: "Wi-Fi connection",
    network: "Network",
    signalRssi: "Signal (RSSI)",
    wifiMac: "Wi-Fi MAC",
    close: "Close",
    calibrationTitle: "Module calibration",
    calibrationDesc:
      'Place the tag on the antenna and click "Start reading" to calibrate.',
    reading: "Reading",
    detectedTags: "Detected tags",
    tag: "tag",
    tags: "tags",
    clear: "Clear",
    bringTagClose: "Bring a tag close to the antenna…",
    noTagDetected: "No tag detected yet.",
    stopReading: "Stop reading",
    startReading: "Start reading",
    stopBeforeClosing: "Stop reading before closing",
    settingsTitle: "Module settings",
    settingsDesc: "Settings for the connected printing hardware.",
    identification: "Identification",
    hardwareId1: "Hardware ID 1",
    readerSerial: "Reader serial",
    hardwareId2: "Hardware ID 2",
    alternative: "(alternative)",
    optional: "Optional",
    rfAndWriting: "RF and writing",
    rfPower: "RF power",
    rfPowerTooltip:
      "RF power applied when writing the tag. Higher = more range, but may unintentionally write nearby tags.",
    min: "Min",
    max: "Max",
    delayMs: "Delay (ms)",
    attempts: "Attempts",
    quickActions: "Quick actions",
    waitingResponse: "Waiting for response…",
    collectModuleInfo: "Collect module info",
    calibrate: "Calibrate",
    test: "Test",
    defineHardwareIdForRoll: "Set a Hardware ID to adjust the roll.",
    adjustRoll: "Adjust roll",
    onlyAdminsCanSave: "Only admins can save",
    save: "Save",
  },
  mfa: {
    twoStepTitle: "Two-step verification",
    codeDialogDesc:
      "Enter the 6-digit code generated by your authenticator app (e.g.: Google Authenticator).",
    invalidCode: "Invalid code. Please try again.",
    cancel: "Cancel",
    validating: "Validating...",
    signIn: "Sign in",
    step1:
      "Install an authenticator app (e.g.: Google Authenticator) on your phone.",
    step2: "Scan the QR Code (or enter the manual key) in the app.",
    step3: "Enter the 6-digit code generated to complete activation.",
    enrollTitle: "Protect your account with two-step verification",
    enrollDesc:
      "We recommend enabling multi-factor authentication (MFA) to increase your account's security. Setup is done on the iNOVACODE portal. Here's how:",
    dontAddNow: "Don't add now",
    configureNow: "Configure now",
  },
};

export default en;
